import React, { forwardRef, useState } from 'react';

import { useDraggable } from '@dnd-kit/core';
import { Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { CircleAlert, Info } from 'lucide-react';
import type { DragField, SelectButtonProps } from 'src/components';
import {
  SelectButton,
  setVariablePanelFocusedField,
  Spacer,
  useScrollFieldIntoView,
  useVariablePanelFocusedField,
} from 'src/components';
import { AlineTooltip } from 'src/components/generic';
import { DragIcon } from 'src/components/icons/DragIcon';
import { NotStaleIcon } from 'src/components/icons/NotStaleIcon';
import { ReSyncIcon } from 'src/components/icons/ReSyncIcon';
import { StaleIcon } from 'src/components/icons/StaleIcon';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import { Apollo } from 'src/gql/generated';
import { composeRefs } from 'src/helpers/compose-refs';
import { useGetCurrentDoc } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { useUpdateIntegrationFieldsInDoc } from './hooks';
import type { DocumentCRMField } from './hooks';
import { getIntegrationFieldStatus } from './integration-field-sync';
import { IntegrationFieldTooltipContent } from './IntegrationFieldTooltip';

interface StyleProps {
  isDisabled?: boolean;
  isSelected?: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { isDisabled, isSelected }) => ({
  button: {
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '&:hover svg': {
      visibility: 'visible',
    },
    backgroundColor: theme.palette.common.white,
    border:
      isSelected && !isDisabled
        ? `1px solid ${theme.palette.primary.main}`
        : `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    cursor: !isDisabled ? 'pointer' : undefined,
    display: 'flex',
    flexDirection: 'column',
    pointerEvents: isDisabled ? 'none' : undefined,
    width: '100%',
  },
  buttonText: {
    alignItems: 'start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  container: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1, 1, 0, 1),
    width: '100%',
  },
  dragIcon: {
    color: isDisabled ? theme.palette.action.disabled : undefined,
    cursor: 'grab',
    visibility: 'hidden',
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    visibility: 'hidden',
  },
  // Keep visible while the interactive tooltip is open (not only on card hover).
  infoIconVisible: {
    visibility: 'visible',
  },
  infoIconWrap: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    marginLeft: theme.spacing(0.5),
  },
  title: {
    marginRight: theme.spacing(0.5),
  },
  /** Centers the info icon on the title text; inline flow would align it to the x-height instead. */
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    minWidth: 0,
  },
  titleText: {
    minWidth: 0,
  },
}));

interface IntegrationFieldButtonProps {
  dragField: DragField;
  integration: Apollo.Integration;
  integrationField: DocumentCRMField;
  className?: string;
  disabledReason?: string;
}

export const IntegrationFieldButton = forwardRef(function IntegrationFieldButton(
  props: IntegrationFieldButtonProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const theme = useTheme();
  const { tabId } = useRequiredEditorRuntime();
  const { scrollFieldIntoView } = useScrollFieldIntoView();
  const focusedField = useVariablePanelFocusedField(tabId);
  const {
    className,
    disabledReason = '',
    dragField,
    integration,
    integrationField,
    ...rest
  } = props;
  const { attributes, listeners, setNodeRef } = useDraggable({
    data: dragField,
    id: dragField.nodeId,
  });
  const isDisabled = Boolean(disabledReason);
  const [isInfoTooltipOpen, setIsInfoTooltipOpen] = useState(false);
  const fieldMatchesIntegration = integrationField.fieldInDocInfo?.integration === integration;
  const isSelected =
    Boolean(integrationField.fieldInDocInfo) &&
    fieldMatchesIntegration &&
    focusedField?.nodeId === integrationField.fieldInDocInfo?.nodeId;
  const { classes } = useStyles({
    isDisabled,
    isSelected,
  });

  return (
    <AlineTooltip arrow placement="top" title={disabledReason}>
      <div>
        <div
          ref={composeRefs<HTMLDivElement>(ref, setNodeRef)}
          aria-selected={isSelected}
          className={clsx(classes.button, className)}
          onClick={() => {
            if (!isDisabled && integrationField.fieldInDocInfo && fieldMatchesIntegration) {
              setVariablePanelFocusedField(tabId, integrationField.fieldInDocInfo);
              scrollFieldIntoView(integrationField.fieldInDocInfo);
            }
          }}
          {...attributes}
          {...listeners}
          {...rest}
        >
          <div className={classes.container}>
            <div className={classes.buttonText}>
              <div className={classes.titleRow}>
                <div className={classes.titleText}>
                  <Typography
                    className={classes.title}
                    color={isDisabled ? 'textSecondary' : undefined}
                    sx={{ display: 'inline', textAlign: 'left' }}
                    variant="subtitle2"
                  >
                    {integrationField.title}
                  </Typography>
                  {integrationField.isCustom && (
                    <Typography color="textSecondary" sx={{ display: 'inline' }}>
                      (custom)
                    </Typography>
                  )}
                </div>
                <AlineTooltip
                  arrow
                  disableInteractive={false}
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: {
                        boxShadow: theme.appShadows.popper,
                        maxWidth: 'none',
                        p: 0,
                        textAlign: 'left',
                      },
                    },
                  }}
                  title={
                    <IntegrationFieldTooltipContent
                      integration={integration}
                      integrationField={integrationField}
                    />
                  }
                  onClose={() => setIsInfoTooltipOpen(false)}
                  onOpen={() => setIsInfoTooltipOpen(true)}
                >
                  <span
                    className={classes.infoIconWrap}
                    onPointerDown={event => event.stopPropagation()}
                  >
                    <Info
                      className={clsx(
                        classes.infoIcon,
                        isInfoTooltipOpen && classes.infoIconVisible
                      )}
                      size={14}
                    />
                  </span>
                </AlineTooltip>
              </div>
              {integrationField.value && (
                <Typography
                  color={isDisabled ? 'textSecondary' : undefined}
                  sx={{ textAlign: 'left' }}
                >
                  {integrationField.value}
                </Typography>
              )}
            </div>
            <Spacer />
            <DragIcon className={classes.dragIcon} />
          </div>
          <IntegrationFieldButtonFooter
            integration={integration}
            integrationField={integrationField}
            isDisabled={isDisabled}
          />
        </div>
      </div>
    </AlineTooltip>
  );
});

const useFooterStyles = makeStyles()(theme => ({
  emptyFooter: {
    height: theme.spacing(1),
  },
  footer: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    width: '100%',
  },
}));

export function IntegrationFieldButtonFooter({
  integration,
  integrationField,
  isDisabled,
}: {
  integration: Apollo.Integration;
  integrationField: DocumentCRMField;
  isDisabled: boolean;
}) {
  const theme = useTheme();
  const { classes } = useFooterStyles();
  const { document } = useGetCurrentDoc();
  const isTemplate = document?.variant === Apollo.DocumentVariant.Template;
  const { updateIntegrationFieldsInDoc } = useUpdateIntegrationFieldsInDoc();
  const fieldMatchesIntegration = integrationField.fieldInDocInfo?.integration === integration;

  if (!integrationField.fieldInDocInfo || !fieldMatchesIntegration)
    return <div className={classes.emptyFooter} />;

  const status = getIntegrationFieldStatus({
    hasIntegrationField: true,
    isStale: integrationField.fieldInDocInfo.isStale,
    isTemplate,
    valueStatus: integrationField.valueStatus,
  });

  if (status === 'valueUnavailable')
    return (
      <div className={classes.footer}>
        <CircleAlert color={theme.palette.error.main} size={16} />
        <Typography noWrap color="error">
          {`Failed to load value from ${integration}`}
        </Typography>
      </div>
    );

  if (status === 'addedToTemplate')
    return (
      <div className={classes.footer}>
        <NotStaleIcon />
        <Typography noWrap color={isDisabled ? 'textSecondary' : undefined}>
          Added to template
        </Typography>
      </div>
    );

  if (status === 'synced')
    return (
      <div className={classes.footer}>
        <NotStaleIcon />
        <Typography noWrap color={isDisabled ? 'textSecondary' : undefined}>
          {`Value synced with ${integration}`}
        </Typography>
      </div>
    );

  return (
    <div className={classes.footer}>
      <StaleIcon />
      <Typography
        noWrap
        color={isDisabled ? 'textSecondary' : undefined}
      >{`Value out of sync with ${integration}`}</Typography>
      <Spacer />
      <SyncIntegrationButton
        buttonProps={{
          disabled: isDisabled,
          onClick: () => {
            updateIntegrationFieldsInDoc([integrationField]);
          },
        }}
        fill={isDisabled ? theme.appColors.rule : undefined}
        title={`Sync variable with ${integration}`}
      />
    </div>
  );
}

interface SyncIntegrationButtonProps {
  buttonProps?: SelectButtonProps;
  fill?: string;
  title?: string;
}

export function SyncIntegrationButton({
  buttonProps,
  fill,
  title = 'Sync document with integration',
}: SyncIntegrationButtonProps): JSX.Element {
  return (
    <AlineTooltip arrow placement="top" title={title}>
      <div>
        <SelectButton {...buttonProps}>
          <ReSyncIcon fill={fill} />
        </SelectButton>
      </div>
    </AlineTooltip>
  );
}
